import { useMemo, useState } from 'react';
import CrossMark from 'components/Icons/CrossMark';
import Modal from '../Modal/Modal';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Cookies from 'js-cookie';

export type RegionKey = 'international' | 'ca' | 'au' | 'nz';

interface Region {
    name: string;
    currency: string;
    flag: {
        rounded: string;
        default: string;
    };
}

export default function RegionSelectionModal() {
    const [openModal, setOpenModal] = useState(true);
    const router = useRouter();
    const [changeRegion, setChangeRegion] = useState(false);

    const closeModal = () => {
        setOpenModal(false);
        Cookies.set('changeRegionFromModal', 'true');
    };

    const AVAILABLE_REGIONS: Record<RegionKey, Region> = {
        au: {
            name: 'Australia',
            currency: 'AUD',
            flag: {
                rounded: '/images/regions/rounded/au.svg',
                default: '/images/regions/default/au.jpg'
            }
        },
        ca: {
            name: 'Canada',
            currency: 'CAD',
            flag: {
                rounded: '/images/regions/rounded/ca.svg',
                default: '/images/regions/default/ca.jpg'
            }
        },
        nz: {
            name: 'New Zealand',
            currency: 'NZD',
            flag: {
                rounded: '/images/regions/rounded/nz.svg',
                default: '/images/regions/default/nz.jpg'
            }
        },
        international: {
            name: 'International',
            currency: 'USD',
            flag: {
                rounded: '/images/regions/rounded/int.svg',
                default: '/images/regions/default/int.jpg'
            }
        }
    };

    const selectedRegion = useMemo(
        () => AVAILABLE_REGIONS[router.locale as RegionKey],
        [router.locale]
    );

    const handleRegionChange = (regionKey: RegionKey) => {
        router.push(router.pathname, router.asPath, { locale: regionKey }).then(() => {
            setOpenModal(false);
            Cookies.set('changeRegionFromModal', 'true');
        });
    };

    const stayCurrentRegion = () => {
        setOpenModal(false);
        Cookies.set('changeRegionFromModal', 'true');
    };

    return (
        <Modal
            open={openModal}
            onClose={closeModal}
            className="relative mx-auto h-[410px] w-[510px] overflow-hidden bg-white 2xl:mt-[120px] mdx:my-auto mdx:h-auto mdx:w-full">
            <span
                className="absolute right-[20px] top-[20px] z-10 cursor-pointer text-brand-black100 transition-colors hover:text-brand-orange"
                onClick={closeModal}>
                <CrossMark fill="currentColor" />
            </span>


            <div className='p-[10px] mt-[30px]'>
                {!changeRegion && (
                    <div>
                        <div className='flex flex-col justify-center items-center'>
                            <h1 className='text-[24px] font-semibold text-[#2A2A2A]'>Your location set to {selectedRegion.name}</h1>

                            <div className='mt-[5px] flex flex-col justify-center items-center'>
                                <p className='text-[#616161] text-[14px] font-normal'>Shop in {selectedRegion.currency} $</p>
                                <p className='text-[#616161] text-[14px] font-normal'>Get shipping options for {selectedRegion.name}</p>
                            </div>
                        </div>

                        <div className='mt-[24px] flex flex-row justify-center items-center gap-[10px]'>
                            <div className='h-[134px] w-[123px] bg-[#ECF9FB] rounded-[6px] flex flex-col items-center'>
                                <div className='mt-[27px]'>
                                    <Image src={selectedRegion.flag.default} alt={selectedRegion.name} width={52} height={36} />
                                </div>
                                <p className='mt-[26px] text-[16px] font-semibold	text-[#2A2A2A]'>{selectedRegion.name}</p>
                            </div>

                            <div className='h-[134px] w-[123px] bg-[#F9F9F9] rounded-[6px] flex flex-col items-center'>
                                <div className='mt-[21px] h-[48px] w-[48px] bg-[#fff] flex justify-center items-center rounded-full'>
                                    <p className='text-[20px] font-semibold	'>$</p>
                                </div>
                                <p className='text-[16px] mt-[20px] font-semibold	text-[#2A2A2A]'>{selectedRegion.currency}</p>
                            </div>
                        </div>

                        <div className='mt-[24px] flex flex-col justify-center items-center'>
                            <button className='w-[330px] h-[48px] bg-[#2A2A2A] text-white font-semibold text-[14px] rounded-[6px]' onClick={() => {
                                stayCurrentRegion();
                            }}>Continue</button>
                            <p className='mt-[14px] text-[#959595] text-[14px] font-normal cursor-pointer' onClick={() => {
                                setChangeRegion(true);
                            }}>Change Location</p>
                        </div>
                    </div>
                )}

                {changeRegion && (
                    <div>
                        <div className='flex flex-col justify-center items-center'>
                            <h1 className='text-[24px] font-semibold text-[#2A2A2A]'>Select Location</h1>
                        </div>

                        <div className='mt-[24px] flex flex-col gap-[4px] justify-center items-center'>
                            {Object.keys(AVAILABLE_REGIONS).map((regionKey) => {
                                const key = regionKey as RegionKey; // Explicitly type the key
                                const region = AVAILABLE_REGIONS[key];

                                if (router.locale === key) {
                                    return (
                                        <div className='h-[54px] p-[16px] flex flex-row bg-[#00AEC7] w-[300px] rounded-[6px] cursor-pointer'
                                            key={key}
                                            onClick={() => {
                                                stayCurrentRegion();
                                            }}>
                                            <div className='flex gap-[16px] flex-1'>
                                                <Image src={region.flag.default} alt={region.name} width={32} height={22} />
                                                <p className='text-[#FFF] text-[14px]'>{region.name}</p>
                                            </div>
                                            <div >
                                                <p className='text-[#FFF] text-[12px]'>{region.currency}</p>
                                            </div>
                                        </div>
                                    )
                                } else {
                                    return (
                                        <div className='h-[54px] p-[16px] flex flex-row bg-[#F9F9F9] w-[300px] rounded-[6px] cursor-pointer'
                                            key={key}
                                            onClick={() => {
                                                handleRegionChange(key);
                                            }}>
                                            <div className='flex gap-[16px] flex-1'>
                                                <Image src={region.flag.default} alt={region.name} width={32} height={22} />
                                                <p className='text-[#2A2A2A] text-[14px]'>{region.name}</p>
                                            </div>
                                            <div>
                                                <p className='text-[#616161] text-[12px]'>{region.currency}</p>
                                            </div>
                                        </div>
                                    )
                                }
                            })}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
